// Credit-monitoring login on the client overview: provider + username always
// visible and copyable; the password is revealed on demand (audited server-side
// as pii.revealed), auto-hides after 30 s, and can be copied. "Update login"
// opens the Edit client form — the saved password stays unless a new one is typed.
import { ClipboardCopy, Eye, EyeOff, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { clients as clientsApi } from "../lib/api";

const LABEL: Record<string, string> = {
  my_free_score_now: "MyFreeScoreNow",
  smart_credit: "SmartCredit",
  identity_iq: "IdentityIQ",
  my_score_iq: "MyScoreIQ",
  not_started: "Not set up",
};

export function CopyButton({ value, label }: { value: string | null; label: string }) {
  const [ok, setOk] = useState(false);
  if (!value) return null;
  return (
    <button
      type="button"
      className="ghost-button"
      style={{ padding: "2px 8px", fontSize: 12 }}
      title={`Copy ${label}`}
      onClick={async () => { try { await navigator.clipboard.writeText(value); setOk(true); setTimeout(() => setOk(false), 1500); } catch { /* clipboard blocked */ } }}
    >
      <ClipboardCopy size={12} aria-hidden="true" /> {ok ? "Copied" : "Copy"}
    </button>
  );
}

export function MonitoringLoginCard({ clientId, provider, username, onUpdate, autoHideMs = 30_000 }: {
  clientId: string;
  provider: string | null | undefined;
  username: string | null;
  onUpdate: () => void;
  autoHideMs?: number;
}) {
  const [revealed, setRevealed] = useState<{ clientId: string; username: string | null; password: string | null } | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const timer = useRef<number | null>(null);

  function hide() {
    setRevealed(null);
    if (timer.current) { window.clearTimeout(timer.current); timer.current = null; }
  }
  useEffect(() => { hide(); return hide; }, [clientId]);

  async function reveal() {
    if (revealed) { hide(); return; }
    setLoading(true); setErr(null);
    try {
      const v = await clientsApi.revealMonitoring(clientId);
      setRevealed({ clientId, username: v?.username ?? null, password: v?.password ?? null });
      if (timer.current) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(hide, autoHideMs);
    } catch (e: any) {
      setErr(e?.body?.message || e?.message || "Could not reveal");
    } finally { setLoading(false); }
  }

  const prov = provider || "not_started";
  const shown = revealed && revealed.clientId === clientId ? revealed : null;
  const user = shown?.username ?? username;

  return (
    <div className="monitoring-login">
      <div className="monitoring-login__row">
        <span className="monitoring-login__provider">{LABEL[prov] || prov}</span>
        {prov === "not_started" && <button type="button" className="ghost-button" style={{ padding: "2px 8px", fontSize: 12 }} onClick={onUpdate}>Set up</button>}
      </div>
      {prov !== "not_started" && (
        <>
          <div className="monitoring-login__row">
            <span className="muted small" style={{ minWidth: 68 }}>Login</span>
            <code className="monitoring-login__value">{user || "—"}</code>
            <CopyButton value={user} label="login" />
          </div>
          <div className="monitoring-login__row">
            <span className="muted small" style={{ minWidth: 68 }}>Password</span>
            <code className="monitoring-login__value">{shown ? (shown.password || "(none saved)") : "••••••••"}</code>
            <button type="button" className="ghost-button" style={{ padding: "2px 8px", fontSize: 12 }} onClick={reveal} disabled={loading} title={shown ? "Hide" : "Reveal password (logged)"}>
              {loading ? <Loader2 size={12} className="spin" /> : shown ? <EyeOff size={12} aria-hidden="true" /> : <Eye size={12} aria-hidden="true" />} {shown ? "Hide" : "Reveal"}
            </button>
            {shown && <CopyButton value={shown.password} label="password" />}
          </div>
          <div className="monitoring-login__row">
            <button type="button" className="ghost-button" style={{ padding: "2px 8px", fontSize: 12 }} onClick={onUpdate}>Update login</button>
            <span className="muted small">Saved password stays unless you type a new one.</span>
          </div>
        </>
      )}
      {err && <span className="revealable-error">{err}</span>}
    </div>
  );
}
